/**
 * LLM services for the Mandate transition governance module.
 *
 * Two capabilities:
 *   1. reflectOnHypothesis — names the organizing theme of a Day 100 hypothesis
 *      (registry: mandate_hypothesis_reflection)
 *   2. connectPriorityToOutcome — names the causal link between a priority and
 *      the Day 100 outcome (registry: mandate_priority_connection)
 *
 * Both are advisory-only narrators: they never write to the store, and both
 * degrade gracefully (return null) when the capability is disabled or no LLM
 * provider is reachable — the surrounding UI must treat a null return as
 * "say nothing," never as an error.
 *
 * Registry: src/lib/ai/aiCapabilityRegistry.ts
 */

import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import type { STaRSSituation } from '../types/mandate';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

const SITUATION_LABELS: Record<STaRSSituation, string> = {
  startup: 'Startup (building something new)',
  turnaround: 'Turnaround (fixing something broken)',
  realignment: 'Realignment (redirecting something that works)',
  sustaining: 'Sustaining Success (growing something strong)',
};

const REFLECTION_SYSTEM_PROMPT =
  "You are a trusted executive advisor helping a leader clarify their first 100 days. Be precise, not flattering. Name the single organizing theme you actually hear — not a summary of their words. Respond in 1-2 sentences only.";

const CONNECTION_SYSTEM_PROMPT =
  'You help leaders connect their tactical priorities to their strategic outcomes. Be precise and specific. One sentence only. No hedging.';

/**
 * Reflects back the organizing theme of a Day 100 hypothesis.
 * Returns null when the hypothesis is too short, the capability is disabled,
 * or no LLM provider is reachable.
 */
export async function reflectOnHypothesis(
  hypothesis: string,
  situationType?: STaRSSituation | null,
): Promise<string | null> {
  if (!isAiCapabilityEnabled('mandate_hypothesis_reflection')) return null;
  if (hypothesis.trim().length < 30) return null;

  const situationLine = situationType ? `Transition type: ${SITUATION_LABELS[situationType]}` : '';

  const userPrompt = [
    situationLine,
    `Day 100 hypothesis: "${hypothesis.trim()}"`,
    '',
    "In 1-2 sentences, name the single organizing theme of what this person is trying to achieve — compress it to its essential idea, don't just summarize the words. Start with exactly \"What I'm hearing is that your Day 100 is about\" and end with \"Is that right?\"",
  ]
    .filter(Boolean)
    .join('\n');

  try {
    return await callFeatureLlm({
      userPrompt,
      systemPrompt: REFLECTION_SYSTEM_PROMPT,
      conversationId: 'mandate-hypothesis-reflection',
      model: DEFAULT_MODEL,
      temperature: 0.4,
      maxTokens: 200,
    });
  } catch (error) {
    console.warn('[llmMandateService] reflectOnHypothesis failed:', error);
    return null;
  }
}

/**
 * Names the causal relationship between a specific priority and the Day 100 outcome.
 * Returns null when inputs are incomplete, the capability is disabled, or no LLM
 * provider is reachable.
 */
export async function connectPriorityToOutcome(
  priorityTitle: string,
  rationale: string,
  outcomeHypothesis: string,
): Promise<string | null> {
  if (!isAiCapabilityEnabled('mandate_priority_connection')) return null;
  if (!priorityTitle.trim() || !rationale.trim() || !outcomeHypothesis.trim()) return null;

  const userPrompt = [
    `Day 100 outcome: "${outcomeHypothesis.trim()}"`,
    `Priority: "${priorityTitle.trim()}"`,
    `Why this matters now: "${rationale.trim()}"`,
    '',
    'In one sentence, name whether and how this priority moves the leader toward (or away from, or sideways relative to) their Day 100 outcome. Be specific about the causal link. Start with "This" and be direct.',
  ].join('\n');

  try {
    return await callFeatureLlm({
      userPrompt,
      systemPrompt: CONNECTION_SYSTEM_PROMPT,
      conversationId: 'mandate-priority-connection',
      model: DEFAULT_MODEL,
      temperature: 0.4,
      maxTokens: 150,
    });
  } catch (error) {
    console.warn('[llmMandateService] connectPriorityToOutcome failed:', error);
    return null;
  }
}
